import { useEffect } from 'react';

/**
 * Pins a "navigation buttons" element to the bottom of the viewport whenever
 * its sentinel is below the visible area (i.e. the buttons would otherwise be
 * scrolled out of view). When the sentinel scrolls back in, the buttons
 * return to their normal inline flow.
 *
 * @param {React.RefObject<HTMLElement>} sentinelRef - zero-height marker placed
 *   immediately BEFORE the buttons in the DOM. Its getBoundingClientRect().top
 *   is the probe we use to decide between pinned / inline states.
 * @param {React.RefObject<HTMLElement>} buttonsRef - the element that gets pinned.
 * @param {React.RefObject<HTMLElement>} spacerRef  - a zero-height element AFTER
 *   the buttons; we inflate it to the button's height while pinned so the
 *   surrounding layout doesn't jump.
 * @param {unknown[]} deps - any extra values the effect should re-run on.
 */
export default function useNavPin(sentinelRef, buttonsRef, spacerRef, deps = []) {
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const buttons = buttonsRef.current;
    const spacer = spacerRef.current;
    if (!sentinel || !buttons || !spacer) return undefined;

    let isFixed = false;
    let rafId = null;

    const setInline = () => {
      if (!isFixed) return;
      isFixed = false;
      buttons.style.position = '';
      buttons.style.bottom = '';
      buttons.style.left = '';
      buttons.style.right = '';
      buttons.style.transform = '';
      buttons.style.width = '';
      buttons.style.maxWidth = '';
      buttons.style.zIndex = '';
      buttons.style.padding = '';
      buttons.style.background = '';
      buttons.style.pointerEvents = '';
      spacer.style.height = '0px';
    };

    const setFixed = () => {
      if (isFixed) return;
      isFixed = true;
      const h = buttons.offsetHeight;
      spacer.style.height = h + 'px';
      buttons.style.position = 'fixed';
      buttons.style.bottom = '0';
      buttons.style.left = '50%';
      buttons.style.transform = 'translateX(-50%)';
      buttons.style.width = '100%';
      buttons.style.maxWidth = '1440px';
      buttons.style.right = '';
      buttons.style.zIndex = '50';
      buttons.style.padding = '12px 16px';
      buttons.style.background = 'transparent';
      buttons.style.pointerEvents = 'none';
    };

    const check = () => {
      rafId = null;
      const rect = sentinel.getBoundingClientRect();
      // 8px dead zone prevents rapid toggling at the boundary
      if (rect.top <= window.innerHeight - 8) {
        setInline();
      } else if (rect.top > window.innerHeight + 8) {
        setFixed();
      }
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(check);
    };

    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
      // Restore inline style in case the component unmounts while fixed
      setInline();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
